import { useState, useEffect, useMemo } from 'react';
import {
  Key,
  ChartLine,
  CurrencyDollar,
  CheckCircle,
  Copy,
  Plus,
  Trash,
  Book,
  Code,
  PaperPlaneTilt,
  Phone,
  Gauge,
  Lightning,
  Crown,
  Broadcast,
  WebhooksLogo,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useClientData } from '../hooks/useDashboardData';
import { StatCard, StatusBadge, ActiveBadge, LoadingSpinner, DashboardHeader } from '../components/DashboardComponents';
import { LiveUSSDMonitor } from '../components/LiveUSSDMonitor';
import { WebhooksManager } from '../components/WebhooksManager';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import { API_URL } from '../config/api';

// Chart config constants
const chartAxisConfig = { fontSize: 12 };
const chartGridConfig = { strokeDasharray: '3 3', stroke: '#e2e8f0' };

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [newApiKey, setNewApiKey] = useState(null);
  const { analytics, apiKeys, otpSessions, usage, loading, fetchData, createApiKey, revokeApiKey } = useClientData();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateApiKey = async () => {
    try {
      const data = await createApiKey();
      setNewApiKey(data.api_key);
      toast.success('API key created successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create API key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;
    try {
      await revokeApiKey(keyId);
      toast.success('API key revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <DashboardHeader
        title="MojoMoney OTP Gateway"
        subtitle={user?.company_name}
        userName={user?.name}
        onLogout={logout}
        icon={PaperPlaneTilt}
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="overview" data-testid="client-tab-overview" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <ChartLine size={18} className="mr-2" /> Overview
            </TabsTrigger>
            <TabsTrigger value="usage" data-testid="client-tab-usage" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Gauge size={18} className="mr-2" /> Usage & Limits
            </TabsTrigger>
            <TabsTrigger value="api-keys" data-testid="client-tab-api-keys" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Key size={18} className="mr-2" /> API Keys
            </TabsTrigger>
            <TabsTrigger value="live-monitor" data-testid="client-tab-live-monitor" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Broadcast size={18} className="mr-2" /> Live Monitor
            </TabsTrigger>
            <TabsTrigger value="webhooks" data-testid="client-tab-webhooks" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <WebhooksLogo size={18} className="mr-2" /> Webhooks
            </TabsTrigger>
            <TabsTrigger value="transactions" data-testid="client-tab-transactions" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Phone size={18} className="mr-2" /> OTP History
            </TabsTrigger>
            <TabsTrigger value="docs" data-testid="client-tab-docs" className="data-[state=active]:bg-[#0B2545] data-[state=active]:text-white">
              <Book size={18} className="mr-2" /> API Docs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <ClientOverviewStats analytics={analytics} user={user} />
            <ClientActivityChart analytics={analytics} />
          </TabsContent>

          <TabsContent value="usage" className="space-y-6">
            <UsageAndLimitsSection usage={usage} user={user} />
          </TabsContent>

          <TabsContent value="api-keys" className="space-y-6">
            <APIKeysManager 
              apiKeys={apiKeys} 
              onCreateKey={handleCreateApiKey}
              onRevokeKey={handleRevokeKey}
            />
            <NewApiKeyDialog 
              apiKey={newApiKey} 
              onClose={() => setNewApiKey(null)}
              onCopy={copyToClipboard}
            />
          </TabsContent>

          <TabsContent value="live-monitor" className="space-y-6">
            <LiveUSSDMonitor />
          </TabsContent>

          <TabsContent value="webhooks" className="space-y-6">
            <WebhooksManager />
          </TabsContent>

          <TabsContent value="transactions" className="space-y-6">
            <ClientOTPSessionsTable sessions={otpSessions} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="docs" className="space-y-6">
            <APIDocumentation />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Client Overview Stats Component
function ClientOverviewStats({ analytics, user }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <StatCard
        title="Total OTP Requests"
        value={analytics?.summary?.total_otp || 0}
        icon={PaperPlaneTilt}
        subtitle="Last 30 days"
      />
      <StatCard
        title="Verified"
        value={analytics?.summary?.total_verified || 0}
        icon={CheckCircle}
        subtitle={`${analytics?.summary?.success_rate || 0}% success rate`}
        color="green"
      />
      <StatCard
        title="Total Spend"
        value={`R${(analytics?.summary?.total_cost || 0).toFixed(2)}`}
        icon={CurrencyDollar}
        subtitle="Last 30 days"
        color="amber"
      />
      <StatCard
        title="Cost Per SMS"
        value={`R${(user?.cost_per_sms || 0.10).toFixed(2)}`}
        icon={Phone}
        subtitle={`USSD: R${(user?.cost_per_ussd || 0.05).toFixed(2)}`}
      />
    </div>
  );
}

// Usage & Limits Section Component
function UsageAndLimitsSection({ usage, user }) {
  if (!usage) {
    return (
      <Card className="dashboard-card p-8 text-center text-slate-500">
        <p>Loading usage data...</p>
      </Card>
    );
  }

  const tierColors = {
    basic: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-300' },
    pro: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300' },
    enterprise: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300' },
  };
  const tierStyle = tierColors[usage.billing_tier] || tierColors.basic;

  const rateLimitItems = [
    { label: 'Per Minute', limit: usage.rate_limits?.per_minute || 0, used: usage.current_usage?.minute || 0, remaining: usage.remaining?.minute || 0 },
    { label: 'Per Hour', limit: usage.rate_limits?.per_hour || 0, used: usage.current_usage?.hour || 0, remaining: usage.remaining?.hour || 0 },
    { label: 'Per Day', limit: usage.rate_limits?.per_day || 0, used: usage.current_usage?.day || 0, remaining: usage.remaining?.day || 0 },
  ];

  const discount = usage.cost_multiplier < 1 ? Math.round((1 - usage.cost_multiplier) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Tier Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="dashboard-card lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2" data-testid="usage-tier-title">
                  <Crown size={20} weight="duotone" className="text-amber-500" /> Billing Tier
                </CardTitle>
                <CardDescription>Your current plan and usage limits</CardDescription>
              </div>
              <Badge className={`${tierStyle.bg} ${tierStyle.text} border ${tierStyle.border} text-sm px-3 py-1`} data-testid="usage-tier-badge">
                {usage.tier_name || 'Basic'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {rateLimitItems.map((item) => {
              const pct = item.limit > 0 ? Math.round((item.used / item.limit) * 100) : 0;
              const isHigh = pct > 80;
              return (
                <div key={item.label} data-testid={`rate-limit-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-slate-700">{item.label}</span>
                    <span className="text-xs text-slate-500">
                      {item.used} / {item.limit} used
                      <span className="ml-2 font-mono">({item.remaining} remaining)</span>
                    </span>
                  </div>
                  <Progress
                    value={pct}
                    className={`h-2.5 ${isHigh ? '[&>div]:bg-red-500' : '[&>div]:bg-[#0B2545]'}`}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lightning size={20} weight="duotone" className="text-blue-500" /> Plan Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2.5" data-testid="tier-features-list">
              {(usage.tier_features || []).map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle size={16} weight="fill" className="text-emerald-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            {discount > 0 && (
              <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                <p className="text-sm font-medium text-emerald-700" data-testid="tier-discount">
                  {discount}% volume discount applied
                </p>
              </div>
            )}
            {usage.billing_tier !== 'enterprise' && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  Contact your account manager to upgrade your plan for higher limits and better rates.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Period Stats */}
      <Card className="dashboard-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Gauge size={20} weight="duotone" /> Usage Summary (Last {usage.usage_stats?.period_days || 30} Days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-testid="usage-summary-grid">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{usage.usage_stats?.total_requests || 0}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">Total Requests</p>
            </div>
            <div className="text-center p-4 bg-emerald-50 rounded-lg">
              <p className="text-2xl font-bold text-emerald-700">{usage.usage_stats?.total_verified || 0}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">Verified</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{usage.usage_stats?.success_rate || 0}%</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">Success Rate</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-lg">
              <p className="text-2xl font-bold text-amber-700">R{(usage.usage_stats?.total_cost || 0).toFixed(2)}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">Total Spend</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Client Activity Chart Component
function ClientActivityChart({ analytics }) {
  const dailyData = useMemo(() => analytics?.daily_stats || [], [analytics]);

  return (
    <Card className="dashboard-card">
      <CardHeader>
        <CardTitle className="text-lg">OTP Activity (Last 30 Days)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyData}>
              <CartesianGrid {...chartGridConfig} />
              <XAxis dataKey="_id" tick={chartAxisConfig} stroke="#94a3b8" />
              <YAxis tick={chartAxisConfig} stroke="#94a3b8" />
              <Tooltip />
              <Area type="monotone" dataKey="total" stroke="#0B2545" fill="#0B2545" fillOpacity={0.1} name="Total" />
              <Area type="monotone" dataKey="verified" stroke="#10B981" fill="#10B981" fillOpacity={0.2} name="Verified" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// API Keys Manager Component
function APIKeysManager({ apiKeys, onCreateKey, onRevokeKey }) {
  return (
    <Card className="dashboard-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">API Keys</CardTitle>
            <CardDescription>Manage your API keys for OTP integration</CardDescription>
          </div>
          <Button onClick={onCreateKey} className="bg-[#0B2545] hover:bg-[#13315C]" data-testid="create-api-key-button">
            <Plus size={18} className="mr-2" /> Generate Key
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="table-overline">Key Prefix</TableHead>
              <TableHead className="table-overline">Created</TableHead>
              <TableHead className="table-overline">Last Used</TableHead>
              <TableHead className="table-overline">Status</TableHead>
              <TableHead className="table-overline">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((key) => (
              <TableRow key={key.id}>
                <TableCell className="font-mono text-sm">{key.key_prefix}...</TableCell>
                <TableCell className="text-sm">{new Date(key.created_at).toLocaleDateString()}</TableCell>
                <TableCell className="text-sm">{key.last_used ? new Date(key.last_used).toLocaleString() : 'Never'}</TableCell>
                <TableCell><ActiveBadge isActive={key.is_active} /></TableCell>
                <TableCell>
                  {key.is_active && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => onRevokeKey(key.id)}
                      data-testid={`revoke-key-${key.id}`}
                    >
                      <Trash size={16} />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// New API Key Dialog Component
function NewApiKeyDialog({ apiKey, onClose, onCopy }) {
  if (!apiKey) return null;

  return (
    <Dialog open={!!apiKey} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API Key Created</DialogTitle>
          <DialogDescription>
            Copy your API key now. It won't be shown again.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="flex items-center gap-2 p-3 bg-slate-100 rounded-lg">
            <code className="flex-1 font-mono text-sm break-all">{apiKey}</code>
            <Button variant="ghost" size="sm" onClick={() => onCopy(apiKey)}>
              <Copy size={18} />
            </Button>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-[#0B2545] hover:bg-[#13315C]">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Client OTP Sessions Table Component
function ClientOTPSessionsTable({ sessions, onRefresh }) {
  return (
    <Card className="dashboard-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">OTP Sessions</CardTitle>
          <Button variant="outline" size="sm" onClick={onRefresh}>Refresh</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="table-overline">Session ID</TableHead>
              <TableHead className="table-overline">Phone</TableHead>
              <TableHead className="table-overline">Type</TableHead>
              <TableHead className="table-overline">Reference</TableHead>
              <TableHead className="table-overline">Status</TableHead>
              <TableHead className="table-overline">Cost</TableHead>
              <TableHead className="table-overline">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((session) => (
              <TableRow key={session.session_id}>
                <TableCell className="font-mono text-xs">{session.session_id?.slice(0, 8)}...</TableCell>
                <TableCell className="font-mono text-sm">{session.phone_number}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="uppercase text-xs">{session.otp_type}</Badge>
                </TableCell>
                <TableCell className="text-sm">{session.transaction_reference}</TableCell>
                <TableCell><StatusBadge status={session.status} /></TableCell>
                <TableCell className="font-mono text-sm">R{session.cost?.toFixed(2)}</TableCell>
                <TableCell className="text-xs text-slate-500">
                  {new Date(session.created_at).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// API Documentation Component
function APIDocumentation() {
  const [showExample, setShowExample] = useState('generate');

  const codeExamples = useMemo(() => ({
    generate: `curl -X POST "${API_URL}/api/otp/generate" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "+27123456789",
    "transaction_reference": "TXN-001",
    "transaction_details": "Payment of R500",
    "otp_type": "sms"
  }'`,
    verify: `curl -X POST "${API_URL}/api/otp/verify" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "uuid-from-generate-response",
    "otp_code": "123456"
  }'`,
    status: `curl -X GET "${API_URL}/api/otp/status/SESSION_ID" \\
  -H "X-API-Key: YOUR_API_KEY"`,
    ussd: `curl -X POST "${API_URL}/api/otp/ussd-callback" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "uuid-from-generate-response",
    "action": "approve",
    "phone_number": "+27123456789"
  }'`,
  }), []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Code size={20} /> API Reference
            </CardTitle>
            <CardDescription>Complete integration guide for MojoMoney OTP Gateway</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <AuthenticationSection />
            <EndpointsSection onSelectExample={setShowExample} />
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-1">
        <Card className="dashboard-card sticky top-24">
          <CardHeader>
            <CardTitle className="text-lg">Code Example</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="code-block whitespace-pre-wrap text-xs overflow-x-auto">
              {codeExamples[showExample]}
            </pre>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Authentication Section Component
function AuthenticationSection() {
  return (
    <div>
      <h3 className="text-md font-semibold text-slate-800 mb-2">Authentication</h3>
      <p className="text-sm text-slate-600 mb-2">
        All API requests require an API key in the <code className="mono bg-slate-100 px-1 rounded">X-API-Key</code> header.
      </p>
      <div className="code-block">X-API-Key: mojo_xxxxxxxxxxxxxxxxxx</div>
    </div>
  );
}

// Endpoints Section Component
function EndpointsSection({ onSelectExample }) {
  const endpoints = [
    {
      method: 'POST',
      path: '/api/otp/generate',
      description: 'Generate and send an OTP to a phone number',
      example: 'generate',
      params: [
        { name: 'phone_number', desc: 'E.164 format (required)' },
        { name: 'transaction_reference', desc: 'Your reference (required)' },
        { name: 'transaction_details', desc: 'Description (optional)' },
        { name: 'otp_type', desc: '"sms" or "ussd" (default: sms)' },
        { name: 'custom_message', desc: 'Custom SMS text (optional)' },
      ],
    },
    {
      method: 'POST',
      path: '/api/otp/verify',
      description: 'Verify an OTP code',
      example: 'verify',
      params: [
        { name: 'session_id', desc: 'From generate response (required)' },
        { name: 'otp_code', desc: '6-digit code (required)' },
      ],
    },
    {
      method: 'GET',
      path: '/api/otp/status/{session_id}',
      description: 'Get the status of an OTP session',
      example: 'status',
      params: [],
    },
    {
      method: 'POST',
      path: '/api/otp/ussd-callback',
      description: 'Handle USSD accept/reject response',
      example: 'ussd',
      params: [
        { name: 'session_id', desc: 'From generate response (required)' },
        { name: 'action', desc: '"approve" or "reject" (required)' },
        { name: 'phone_number', desc: 'Must match original (required)' },
      ],
    },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-md font-semibold text-slate-800">Endpoints</h3>
      {endpoints.map((endpoint) => (
        <div key={endpoint.path} className="border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge className={endpoint.method === 'GET' ? 'bg-blue-100 text-blue-800' : 'bg-emerald-100 text-emerald-800'}>
              {endpoint.method}
            </Badge>
            <code className="font-mono text-sm">{endpoint.path}</code>
          </div>
          <p className="text-sm text-slate-600 mb-3">{endpoint.description}</p>
          {endpoint.params.length > 0 && (
            <>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Request Body</h4>
              <ul className="text-sm text-slate-600 space-y-1 mb-3">
                {endpoint.params.map((param) => (
                  <li key={param.name}>
                    <code className="mono bg-slate-100 px-1 rounded">{param.name}</code> - {param.desc}
                  </li>
                ))}
              </ul>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => onSelectExample(endpoint.example)}>
            View Example
          </Button>
        </div>
      ))}
    </div>
  );
}
